import React, { useEffect, useState } from 'react';
import { Box, keyframes } from '@mui/material';
import { DeployStepStatus } from '@/types/board';
import {
  PendingCircle,
  InProgressCircle,
  CompletedCircle,
  ErrorCircle,
} from './styles';
import { ANIMATION_TIMING } from './constants';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      border: '2.5px solid var(--dk-accent-line, var(--accent-blue-blue-24, #C2DAF3))',
      borderTopColor: 'var(--Portal-Color-Point-color, var(--dk-accent, var(--accent-blue-blue-100, #06C)))',
      borderRadius: '50%',
      animation: `${spin} 0.75s linear infinite`,
    }}
  />
);

const CheckmarkSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M11.9805 4.97852L6.86335 10.8884L3.98047 8.00547"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WarningSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M8.0625 8.50627V5.98314M8.0625 10.3764V10.3986M11.2518 12.5H4.87316C4.00187 12.5 3.26658 11.9242 3.03524 11.1363C2.93649 10.8 3.05783 10.4497 3.24343 10.1515L6.43276 4.40058C7.17999 3.19981 8.94501 3.19981 9.69224 4.40058L12.8816 10.1515C13.0672 10.4497 13.1885 10.8 13.0898 11.1363C12.8584 11.9242 12.1231 12.5 11.2518 12.5Z"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface DeployStepIconProps {
  status: DeployStepStatus;
  isAnimating?: boolean;
  isExpanding?: boolean;
  iconScale?: number;
  compactInProgress?: boolean;
}

const DeployStepIcon: React.FC<DeployStepIconProps> = ({
  status,
  isAnimating = false,
  isExpanding = false,
  iconScale = 1,
  compactInProgress = false,
}) => {
  // SVG 스케일 상태 (애니메이션용)
  const [svgScale, setSvgScale] = useState(0);
  // 원 수축 상태 (스피너 축소와 분리)
  const [circleShrunk, setCircleShrunk] = useState(false);
  // 확장 애니메이션 상태
  const [expandedSize, setExpandedSize] = useState(20);
  const [spinnerScale, setSpinnerScale] = useState(0);

  // iconScale이 0이 되면:
  // 1. 스피너 축소 즉시 시작
  // 2. 원 수축은 CIRCLE_SHRINK_DELAY 후 시작
  // 3. SVG는 SVG_SPRING 시점에 스케일 애니메이션
  useEffect(() => {
    if (isAnimating && iconScale === 0) {
      // 원 수축 시작
      const shrinkTimer = setTimeout(() => {
        setCircleShrunk(true);
      }, ANIMATION_TIMING.CIRCLE_SHRINK_DELAY);

      // SVG 스프링 애니메이션
      const svgTimer = setTimeout(() => {
        setSvgScale(1);
      }, ANIMATION_TIMING.SVG_SPRING);

      return () => {
        clearTimeout(shrinkTimer);
        clearTimeout(svgTimer);
      };
    }
  }, [isAnimating, iconScale]);

  // 애니메이션 시작 시 상태 리셋
  useEffect(() => {
    if (isAnimating) {
      setCircleShrunk(false);
      setSvgScale(0);
    }
  }, [isAnimating]);

  // pending → in_progress 확장 애니메이션
  useEffect(() => {
    if (isExpanding) {
      // 초기 상태: 20px, spinner scale(0)
      setExpandedSize(20);
      setSpinnerScale(0);

      // 다음 프레임에서 확장 시작
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setExpandedSize(48);
          // 스피너 등장 지연
          setTimeout(() => {
            setSpinnerScale(1);
          }, ANIMATION_TIMING.SPINNER_DELAY);
        });
      });
    } else if (status === 'in_progress' && !isAnimating) {
      // 확장 완료 후 정상 상태
      setExpandedSize(48);
      setSpinnerScale(1);
    }
  }, [isExpanding, status, isAnimating]);

  // 애니메이션 중일 때: 진행중 아이콘에서 완료 아이콘으로 전환 애니메이션
  if (isAnimating) {
    const isError = status === 'error';
    const bgColor = isError ? '#F55060' : 'var(--Portal-Color-Point-color, var(--accent-blue-blue-100, #06C))';
    const circleSize = compactInProgress ? 20 : 48;

    return (
      <Box
        sx={{
          width: circleShrunk ? 20 : circleSize,
          height: circleShrunk ? 20 : circleSize,
          borderRadius: '50%',
          backgroundColor: circleShrunk ? bgColor : 'var(--dk-bg, #FFFFFF)',
          outline: circleShrunk ? 'none' : '1px solid var(--accent-blue-blue-24, #C2DAF3)',
          outlineOffset: -1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
          zIndex: 1,
          transition:
            'width 0.38s cubic-bezier(0.34,1.56,0.64,1), height 0.38s cubic-bezier(0.34,1.56,0.64,1), background-color 0.25s ease',
        }}
      >
        {circleShrunk ? (
          // 체크마크 또는 경고 아이콘 - scale(0)에서 scale(1)로 전환
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: `scale(${svgScale})`,
              transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            {isError ? <WarningSvg /> : <CheckmarkSvg />}
          </Box>
        ) : (
          // Spinner 축소 애니메이션 (iconScale로 제어, 즉시 시작)
          <Box
            sx={{
              transform: `scale(${iconScale})`,
              opacity: iconScale,
              transition: 'transform 0.3s ease-in, opacity 0.25s ease-in',
            }}
          >
            <Spinner />
          </Box>
        )}
      </Box>
    );
  }

  switch (status) {
    case 'pending':
      return <PendingCircle />;

    case 'in_progress':
      if (compactInProgress) {
        return <PendingCircle />;
      }
      // 확장 애니메이션 중일 때
      if (isExpanding) {
        return (
          <Box
            sx={{
              width: expandedSize,
              height: expandedSize,
              borderRadius: '50%',
              backgroundColor: 'var(--dk-bg, #FFFFFF)',
              outline: '1px solid var(--dk-accent-line, var(--accent-blue-blue-24, #C2DAF3))',
              outlineOffset: -1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              position: 'relative',
              zIndex: 1,
              transition:
                'width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), height 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <Box
              sx={{
                transform: `scale(${spinnerScale})`,
                opacity: spinnerScale,
                transition:
                  'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease',
              }}
            >
              <Spinner />
            </Box>
          </Box>
        );
      }
      return (
        <InProgressCircle>
          <Spinner />
        </InProgressCircle>
      );

    case 'completed':
      return (
        <CompletedCircle>
          <CheckmarkSvg />
        </CompletedCircle>
      );

    case 'error':
      return (
        <ErrorCircle>
          <WarningSvg />
        </ErrorCircle>
      );

    default:
      return <PendingCircle />;
  }
};

export default DeployStepIcon;
